import { ReactElement, useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  ArrowLeft,
  X as CloseIcon,
  Plus as PlusIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Trash2,
  ImagePlus as AttachIcon
} from 'lucide-react';

import { paths } from 'configuration/paths';
import {
  getAttachmentsOfType,
  getSubsection,
  isVisible,
  sortSurvey,
  trimInvisibleAnswers,
  updateAnswer,
  updatePageAnswers,
  updateAttachmentsOfType,
  updateSubsectionAttachments,
  updateSubsectionStatus,
  getProductToPageMap,
  filterDependent,
  deletePageAnswers
} from 'utils/survey';
import { TranslationsKeys } from 'types/translation';
import { IMAGE_CATEGORY, VIDEO_CATEGORY } from 'types/attachmentTypes';
import { Survey, SurveyQuestion } from 'types/survey';
import { Answer, Page, Attachment, Status } from 'types/surveyResponse';
import { useTranslate } from 'hooks/useTranslate';
import { useSurveyTranslation } from 'hooks/useSurveyTranslation';
import { useSurveyDefinitionQuery, useSurveyResponseQuery } from 'hooks/queries';
import { useUpdateSurveyResponse } from 'hooks/mutations';
import { Header } from 'components/Header';
import { Spinner } from 'components/Spinner';
import { Question } from 'components/Question';
import { TextQuestion } from 'components/Question/components/TextQuestion';
import { Footer } from 'components/Footer';
import { FullscreenContainer } from 'components/FullscreenContainer';
import { Images } from 'components/Images';
import { Modal } from 'components/Modal';
import { Button } from 'components/ui/Button';
import { Radio } from 'components/ui/Radio';
import styles from './SurveySection.module.scss';

const SkipSectionReason = ({
  onSelect,
  onCancel
}: {
  onSelect: (reason: string) => void;
  onCancel: () => void;
}): ReactElement => {
  const [reason, setReason] = useState<string | undefined>(undefined);
  const { t } = useTranslate();
  const options = [
    t(TranslationsKeys.surveySectionPage_skipSectionNotRelevant),
    t(TranslationsKeys.surveySectionPage_skipSectionNotObserved),
    t(TranslationsKeys.surveySectionPage_skipSectionOther)
  ];

  return (
    <FullscreenContainer>
      <div className={styles.surveySectionSkipSection}>
        <div className={styles.surveySectionSkipSectionHeading}>
          <span>{t(TranslationsKeys.surveySectionPage_skipSection)}</span>
          <button onClick={onCancel}>
            <CloseIcon />
          </button>
        </div>
        <div className={styles.surveySectionSkipSectionBody}>
          <p className={styles.surveySectionSkipSectionTitle}>
            {t(TranslationsKeys.surveySectionPage_skipSectionTitle)}
          </p>
          <p className={styles.surveySectionSkipSectionDescription}>
            {t(TranslationsKeys.surveySectionPage_skipSectionDescription)}
          </p>
          {options.map((o) => (
            <Radio key={o} label={o} isChecked={o === reason} onSelect={() => setReason(o)} />
          ))}
        </div>
        <div className={styles.surveySectionSkipSectionFooter}>
          <Button
            className={styles.surveySectionFooterButton}
            state={reason ? 'normal' : 'disabled'}
            onClick={() => onSelect(reason!)}>
            {t(TranslationsKeys.common_close)}
          </Button>
        </div>
      </div>
    </FullscreenContainer>
  );
};

const SurveySection = (): ReactElement => {
  const { surveyId, sectionId, subsectionId } = useParams();
  const navigate = useNavigate();
  const { t, activeLanguage } = useTranslate();
  const surveyResponseQuery = useSurveyResponseQuery(surveyId);
  const surveyResponse = surveyResponseQuery.data;
  const surveyDefinitionQuery = useSurveyDefinitionQuery(
    surveyResponse ? surveyResponse.surveyDefinitionId : undefined
  );
  const surveyDefinition = useMemo(
    () => (surveyDefinitionQuery.data ? sortSurvey(surveyDefinitionQuery.data) : undefined),
    [surveyDefinitionQuery.data]
  );
  const surveySubsection = useMemo(
    () => (surveyDefinition ? getSubsection(surveyDefinition, sectionId!, subsectionId!) : undefined),
    [surveyDefinition]
  );
  const responseSubsection = useMemo(
    () =>
      surveyResponse
        ? getSubsection(surveyResponse, sectionId!, subsectionId!) || {
            subsectionId,
            answers: [],
            pages: [],
            status: Status.NotStarted,
            statusHistory: [{ status: Status.NotStarted, changedAt: new Date().toISOString() }],
            attachments: []
          }
        : undefined,
    [surveyResponse]
  );
  const isLoading = surveyResponseQuery.isLoading || surveyDefinitionQuery.isLoading;
  const isError = surveyResponseQuery.isError || surveyDefinitionQuery.isError;
  const ts = useSurveyTranslation(surveyDefinition);
  const updateSurveyResponse = useUpdateSurveyResponse();
  const [showSkipSectionReason, setShowSkipSectionReason] = useState(false);
  const [hasProductQuestions, setHasProductQuestions] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<Record<string, string>>({});
  const [showProductQuestions, setShowProductQuestions] = useState<boolean>(false);
  const [pageAnswers, setPageAnswers] = useState<{ pageNumber?: number; answers: Answer[] }>({ answers: [] });
  const [updatingPageAnswers, setUpdatingPageAnswers] = useState<boolean>(false);
  const [visibleAdditionalInputs, setVisibleAdditionalInputs] = useState<string[]>([]);
  const [confirmProductPageDeletion, setConfirmProductPageDeletion] = useState<number | null>(null);
  const productPageMap = getProductToPageMap(surveySubsection?.questions || []);

  const showQuestion = (q: SurveyQuestion) => {
    // Skip "Products in use"-question if we have product questions already presented with separate logic
    // Temporarily skip "call-to-action" typed questions, these will be handled on a later task
    // Check that "choice" typed questions have options to choose from

    return (
      (hasProductQuestions ? q.name !== 'products_in_use' : true) &&
      q.type !== 'call-to-action' &&
      (q.type === 'choice' ? q.options?.length && q.options?.length > 0 : true)
    );
  };

  const toggleVisibleAdditionalInput = (questionId: string) => {
    if (visibleAdditionalInputs.includes(questionId)) {
      // Cancel adding custom product question answer
      setPageAnswers((prev) => ({
        ...prev,
        answers: prev.answers.filter((a) => a.questionId !== questionId)
      }));
      setVisibleAdditionalInputs((prev) => prev.filter((id) => id !== questionId));
    } else {
      setVisibleAdditionalInputs((prev) => [...prev, questionId]);
    }
  };

  const onUpdateAnswer = useCallback(
    async (answer: Answer) => {
      try {
        answer.language = activeLanguage;
        const newResponse = updateAnswer(surveyResponse!, sectionId!, subsectionId!, answer);
        await updateSurveyResponse.mutateAsync(trimInvisibleAnswers(surveyDefinition!, newResponse));
      } catch (error) {
        console.error('Error updating answer:', error);
      }
    },
    [surveyResponse, sectionId, subsectionId, updateSurveyResponse]
  );

  const onUpdatePageAnswers = useCallback(
    async (answers: Answer[], page: string, pageNumber: number) => {
      try {
        await updateSurveyResponse.mutateAsync(
          updatePageAnswers(surveyResponse!, sectionId!, subsectionId!, answers, page, pageNumber)
        );
      } catch (error) {
        console.error('Error updating page answers:', error);
      }
    },
    [surveyResponse, sectionId, subsectionId, updateSurveyResponse]
  );

  const onDeletePageAnswers = useCallback(
    async (pageNumber: number) => {
      try {
        await updateSurveyResponse.mutateAsync(
          deletePageAnswers(surveyResponse!, sectionId!, subsectionId!, pageNumber)
        );
      } catch (error) {
        console.error('Error deleting page answers:', error);
      }
    },
    [surveyResponse, sectionId, subsectionId, updateSurveyResponse]
  );

  const onUpdateAttachments = useCallback(
    async (attachments: Attachment[]) => {
      await updateSurveyResponse.mutateAsync(
        updateSubsectionAttachments(surveyResponse!, sectionId!, subsectionId!, attachments)
      );
    },
    [surveyResponse, sectionId, subsectionId, updateSurveyResponse]
  );

  const onFinish = async (isSkipped: boolean, reason?: string) => {
    await updateSurveyResponse.mutateAsync(
      updateSubsectionStatus(surveyDefinition!, surveyResponse!, sectionId!, subsectionId!, isSkipped, reason)
    );
    navigate(paths.survey.replace(':surveyId', surveyResponse!.responseId));
  };

  useEffect(() => {
    setHasProductQuestions(
      !!surveySubsection?.pages?.length && surveySubsection?.pages?.length > 0 && typeof productPageMap !== 'undefined'
    );
  }, [surveySubsection?.pages]);

  return (
    <>
      <Header navigationItems={[]}>
        {isLoading && <Spinner />}
        {surveySubsection && (
          <div className="d-flex flex-row align-items-center">
            <button className={styles.surveySectionHeaderBack} onClick={() => onFinish(false)}>
              <ArrowLeft />
            </button>
            <h3 className={styles.surveySectionHeaderTitle}>{ts(surveySubsection.name)}</h3>
          </div>
        )}
        {isError && <div className="m-2 fs-6 text-danger">{t(TranslationsKeys.surveyPage_loadingError)}</div>}
        <button className={styles.surveySectionHeaderSkipSection} onClick={() => setShowSkipSectionReason(true)}>
          {t(TranslationsKeys.surveySectionPage_skipSection)}
        </button>
      </Header>

      {surveySubsection && responseSubsection && (
        <div className={styles.surveySection}>
          {/* Product selection */}
          {hasProductQuestions && (
            <div className={styles.surveySectionProductSelectionContainer}>
              <div>
                <h2 className={styles.surveySectionHeading}>{t(TranslationsKeys.surveySectionPage_productsInUse)}</h2>
                <div className={styles.surveySectionProductSelection}>
                  {Object.keys(productPageMap).map((product) => (
                    <div key={product} className={styles.surveySectionPageProduct}>
                      <Button
                        className={styles.surveySectionProductButton}
                        variant="secondary"
                        onClick={() => {
                          setSelectedProduct({ name: product, page: productPageMap[product] });
                          setShowProductQuestions(true);
                        }}>
                        <PlusIcon />
                        <span>{ts(productPageMap[product])}</span>
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              {/* User's product responses */}
              {responseSubsection.pages && responseSubsection.pages.length > 0 && (
                <div className={styles.surveySectionProductAnswers}>
                  {responseSubsection.pages.map((p: Page, index: number) => (
                    <div key={`${p.page}-${index}`} className={styles.surveySectionProductAnswersItem}>
                      <div className={styles.surveySectionProductAnswersItemTitle}>
                        <span>{ts(p.page)}</span>
                        <span className={styles.surveySectionProductAnswersItemButtons}>
                          <button
                            onClick={() => {
                              setSelectedProduct({ name: p.page, page: p.page });
                              setPageAnswers({ pageNumber: index, answers: p.answers });
                              setUpdatingPageAnswers(true);
                              setShowProductQuestions(true);
                            }}>
                            <EditIcon />
                          </button>
                          <button
                            onClick={() => {
                              setConfirmProductPageDeletion(index);
                            }}>
                            <DeleteIcon />
                          </button>
                        </span>
                      </div>
                      <div className={styles.surveySectionProductAnswersItemAnswers}>
                        {p.answers
                          .filter((a) => a.value !== a.questionId)
                          .map((a, index) => (
                            <div
                              key={`${a.questionId}-${index}`}
                              className={styles.surveySectionProductAnswersItemAnswer}>
                              <span>
                                {typeof a.value === 'string' || typeof a.value === 'number'
                                  ? a.value
                                  : a.value?.join(', ')}
                              </span>
                              {index < p.answers.filter((a) => a.value !== a.questionId).length - 1 && <span>,</span>}
                            </div>
                          ))}
                        {p.answers.find(
                          (a) =>
                            a.attachments?.filter(
                              (at) => at.mimeType.includes(IMAGE_CATEGORY) || at.mimeType.includes(VIDEO_CATEGORY)
                            ).length
                        ) && <AttachIcon />}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Subsection level questions */}
          {!hasProductQuestions && <h1 className={styles.surveySectionHeading}>{ts(surveySubsection.name)}</h1>}
          {surveySubsection.questions?.map((q) =>
            showQuestion(q) && isVisible(q, surveyResponse!) ? (
              <Question
                key={q.questionId}
                surveyDefinition={surveyDefinition!}
                question={q}
                answer={
                  responseSubsection.answers.find((qr) => qr.questionId === q.questionId) || {
                    questionId: q.questionId,
                    value: null
                  }
                }
                siblingAnswers={responseSubsection.answers}
                onChange={onUpdateAnswer}
              />
            ) : null
          )}
          {surveySubsection.allowImages && (
            <div className={styles.surveySectionImages}>
              <Images
                label={t(TranslationsKeys.images_images)}
                images={getAttachmentsOfType(responseSubsection.attachments, [IMAGE_CATEGORY, VIDEO_CATEGORY])}
                onImagesChanged={(images) =>
                  onUpdateAttachments(
                    updateAttachmentsOfType(responseSubsection?.attachments, [IMAGE_CATEGORY, VIDEO_CATEGORY], images)
                  )
                }
              />
            </div>
          )}
        </div>
      )}

      <Footer>
        <Button className={styles.surveySectionFooterButton} onClick={() => onFinish(false)}>
          {t(TranslationsKeys.surveySectionPage_close)}
        </Button>
      </Footer>

      {showSkipSectionReason && (
        <SkipSectionReason
          onSelect={(reason: string) => onFinish(true, reason)}
          onCancel={() => setShowSkipSectionReason(false)}
        />
      )}

      {/* Product input questions */}
      {surveySubsection && responseSubsection && selectedProduct && showProductQuestions && (
        <FullscreenContainer>
          <div className={styles.surveySectionProductInputView}>
            <div className={styles.surveySectionProductInputViewHeading}>
              <span>
                {updatingPageAnswers
                  ? t(TranslationsKeys.surveySectionPage_updateProduct)
                  : t(TranslationsKeys.surveySectionPage_addProduct)}
              </span>
              <button onClick={() => setShowProductQuestions(false)}>
                <CloseIcon />
              </button>
            </div>
            <div className={styles.surveySectionProductInputViewBody}>
              <h3 className={styles.surveySectionProductInputViewBodyTitle}>{ts(selectedProduct.name)}</h3>
              <div key={selectedProduct.name} className={styles.surveySectionProductInputViewBodyQuestions}>
                {surveySubsection.pages
                  ?.find((p) => p.page === selectedProduct.page)
                  ?.questions.filter((q) => filterDependent(q, pageAnswers.answers))
                  .map((q) => {
                    const currentAnswer = pageAnswers.answers.find((qr) => qr.questionId === q.questionId);
                    const isCustomAnswer = !q.options?.some((o) => o.value === currentAnswer?.value?.toString());
                    const onChange = (answer: Answer) => {
                      const pageNumber = pageAnswers?.pageNumber ?? responseSubsection.pages?.length;
                      answer.language = activeLanguage;
                      // value cannot be null, so when there's only an attachement, we need to use the question's ID
                      answer.value = q.type === 'attachment_only' ? q.questionId : answer.value;
                      const updatedAnswers = [
                        ...pageAnswers.answers.filter((qr) => qr.questionId !== answer.questionId),
                        answer
                      ];
                      setPageAnswers({
                        pageNumber,
                        answers: updatedAnswers
                      });
                    };

                    return (
                      <div key={q.questionId}>
                        <Question
                          surveyDefinition={surveyDefinition!}
                          question={q}
                          answer={
                            currentAnswer || {
                              questionId: q.questionId,
                              value: null
                            }
                          }
                          siblingAnswers={pageAnswers.answers}
                          disabled={visibleAdditionalInputs.includes(q.questionId)}
                          onChange={onChange}
                        />
                        {q.includeAdditionalTextInput && (
                          <div className={styles.surveySectionProductInputViewBodyQuestionsAdditionalInput}>
                            {!visibleAdditionalInputs.includes(q.questionId) && (
                              <Button
                                variant="tertiary"
                                size="small"
                                onClick={() =>
                                  toggleVisibleAdditionalInput(q.questionId)
                                }>{`${t(TranslationsKeys.common_addNew)} ${ts(q.name).toLowerCase()}`}</Button>
                            )}
                            {visibleAdditionalInputs.includes(q.questionId) && (
                              <>
                                <label htmlFor={`question-${q.questionId}`}>
                                  {`${t(TranslationsKeys.common_addNew)} ${ts(q.name).toLowerCase()}`}
                                </label>
                                <div
                                  className={styles.surveySectionProductInputViewBodyQuestionsAdditionalInputWrapper}>
                                  <TextQuestion
                                    surveyDefinition={surveyDefinition as Survey}
                                    question={q}
                                    answer={
                                      isCustomAnswer && currentAnswer
                                        ? currentAnswer
                                        : {
                                            questionId: q.questionId,
                                            value: null
                                          }
                                    }
                                    onChange={onChange}
                                  />
                                  <Button
                                    variant="tertiary"
                                    size="small"
                                    onClick={() => toggleVisibleAdditionalInput(q.questionId)}>
                                    {t(TranslationsKeys.common_cancel)}
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
            <div className={styles.surveySectionProductInputViewFooter}>
              <Button
                className={styles.surveySectionProductInputViewFooterButton}
                variant="secondary"
                onClick={() => {
                  setShowProductQuestions(false);
                  setUpdatingPageAnswers(false);
                  setPageAnswers({ answers: [] });
                }}>
                {t(TranslationsKeys.common_close)}
              </Button>
              <Button
                className={styles.surveySectionProductInputViewFooterButton}
                variant="primary"
                onClick={() => {
                  setShowProductQuestions(false);
                  (async () => {
                    await onUpdatePageAnswers(
                      pageAnswers.answers,
                      selectedProduct.page,
                      pageAnswers.pageNumber ?? responseSubsection?.pages?.length ?? 0
                    );
                    setPageAnswers({ answers: [] });
                    setUpdatingPageAnswers(false);
                  })();
                }}>
                {updatingPageAnswers
                  ? t(TranslationsKeys.surveySectionPage_updateProduct)
                  : t(TranslationsKeys.surveySectionPage_addProduct)}
              </Button>
            </div>
          </div>
        </FullscreenContainer>
      )}

      {/* Product deletion confirmation */}
      {confirmProductPageDeletion !== null && (
        <Modal
          title={t(TranslationsKeys.surveySectionPage_deleteProduct)}
          description={t(TranslationsKeys.surveySectionPage_deleteProductConfirm)}
          primaryButtonLabel={
            <div className="d-flex justify-content-center align-items-center gap-2">
              <Trash2 />
              {t(TranslationsKeys.common_delete)}
            </div>
          }
          primaryButtonVariant="danger"
          secondaryButtonLabel={t(TranslationsKeys.common_close)}
          onPrimaryClick={async () => {
            setConfirmProductPageDeletion(null);
            await onDeletePageAnswers(confirmProductPageDeletion);
          }}
          onSecondaryClick={() => setConfirmProductPageDeletion(null)}
        />
      )}
    </>
  );
};

export default SurveySection;
